/**
 * Typsicherer API-Client für die Domownik-API.
 * Liest den Access-Token aus localStorage und fügt ihn automatisch hinzu.
 * Setzt zusätzlich ein Cookie für die Next.js Middleware (Auth-Guard).
 */

const API_BASE = process.env.NEXT_PUBLIC_API_URL ?? 'http://127.0.0.1:3001/api/v1'

class ApiError extends Error {
  public traceId?: string
  public errorCode?: string

  constructor(
    public status: number,
    message: string,
    options?: { traceId?: string; errorCode?: string }
  ) {
    super(message)
    this.name = 'ApiError'
    if (options?.traceId !== undefined) {
      this.traceId = options.traceId
    }
    if (options?.errorCode !== undefined) {
      this.errorCode = options.errorCode
    }
  }
}

// ---------------------------------------------------------------------------
// Token-Verwaltung
// ---------------------------------------------------------------------------

function getToken(): string | null {
  if (typeof window === 'undefined') return null
  return localStorage.getItem('accessToken')
}

function setToken(token: string): void {
  if (typeof window === 'undefined') return
  localStorage.setItem('accessToken', token)
  // Cookie für Middleware setzen (nicht httpOnly, damit JS darauf zugreifen kann)
  document.cookie = `accessToken=${token}; path=/; max-age=${15 * 60}; SameSite=Strict`
}

function clearToken(): void {
  if (typeof window === 'undefined') return
  localStorage.removeItem('accessToken')
  document.cookie = 'accessToken=; path=/; max-age=0; SameSite=Strict'
}

/** Zur Login-Seite weiterleiten (mit optionalem Redirect-Parameter) */
function redirectToLogin(): void {
  if (typeof window === 'undefined') return
  clearToken()
  const redirect = window.location.pathname
  if (redirect === '/login') return
  const params = new URLSearchParams({ redirect, expired: '1' })
  window.location.href = `/login?${params.toString()}`
}

// ---------------------------------------------------------------------------
// HTTP-Client
// ---------------------------------------------------------------------------

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options?: RequestInit
): Promise<T> {
  const token = getToken()

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }

  const requestInit: RequestInit = {
    method,
    headers,
    credentials: 'include',
    ...options,
  }

  if (body !== undefined) {
    requestInit.body = JSON.stringify(body)
  }

  const res = await fetch(`${API_BASE}${path}`, requestInit)

  if (!res.ok) {
    // Bei 401: Token ist ungültig/abgelaufen → zur Login-Seite
    if (res.status === 401 && !path.includes('/auth/')) {
      redirectToLogin()
      // Nie erreicht, da redirectToLogin die Seite wechselt
      throw new ApiError(401, 'Sitzung abgelaufen')
    }

    const error = await res.json().catch(() => ({ error: 'Unbekannter Fehler' })) as {
      error?: string
      traceId?: string
      errorCode?: string
    }

    const traceId = res.headers.get('x-request-id') ?? error.traceId

    const errorOptions: { traceId?: string; errorCode?: string } = {}
    if (traceId !== undefined) {
      errorOptions.traceId = traceId
    }
    if (error.errorCode !== undefined) {
      errorOptions.errorCode = error.errorCode
    }

    throw new ApiError(res.status, error.error ?? res.statusText, errorOptions)
  }

  // 204 No Content hat keinen Body
  if (res.status === 204) {
    return undefined as T
  }

  return res.json() as Promise<T>
}

export const apiClient = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),
}

export { ApiError, setToken, clearToken, getToken, redirectToLogin }
